import React from 'react';
import { clsx } from 'clsx';
import { Menu as BaseMenu } from '@base-ui-components/react/menu';

import ActionsArrowDown from '@strava/icons/ActionsArrowDownNormalXsmall';

import { Button } from '../Button/Button';
import { Element } from '../Element/Element';
import { Typography } from '../Typography/Typography';

import styles from './Menu.module.scss';

type CssVarsKeys = '--z-index-override';

type CssPropsWithVars = React.CSSProperties &
  Record<CssVarsKeys, string | number | undefined>;

// Anatomy
// <Menu>
//   <Menu.Trigger/>
//   <Menu.Portal>
//     <Menu.Positioner>
//       <Menu.Popup>
//         <Menu.Item/>
//         <Menu.Separator/>
//       </Menu.Popup>
//     </Menu.Positioner>
//   </Menu.Portal>
// </Menu>

/**
 * =====================================
 * Menu (Root)
 *
 * Sets up a Menu with a button
 * trigger and a List that holds the
 * child Menu Items
 * =====================================
 * */
export const Menu = ({
  text,
  positionerProps,
  menuTriggerProps,
  portalProps,
  popupProps,
  children,
  zIndex,
  ...props
}: Menu.Props) => {
  return (
    <BaseMenu.Root modal={false} {...props}>
      <MenuTrigger {...menuTriggerProps} text={text} />
      <MenuList
        positionerProps={positionerProps}
        portalProps={portalProps}
        {...popupProps}
        zIndex={zIndex}
      >
        {children}
      </MenuList>
    </BaseMenu.Root>
  );
};
Menu.displayName = 'Menu';

export namespace Menu {
  export type Props = Omit<BaseMenu.Root.Props, 'orientation'> & {
    /** The trigger button text. */
    text?: string;
    /** Props to pass to the MenuTrigger subcomponent. */
    menuTriggerProps?: Omit<MenuTrigger.Props, 'text' | 'children'>;
    /** Props passed to the base-ui Positioner component. [See Positioner Arg Table](https://base-ui.com/react/components/menu#positioner) */
    positionerProps?: BaseMenu.Positioner.Props;
    /** Props passed to the base-ui Portal component. [See Portal Arg Table](https://base-ui.com/react/components/menu#portal) */
    portalProps?: MenuList.Props['portalProps'];
    /** Props passed to the base-ui Popup component. [See Portal Arg Table](https://base-ui.com/react/components/menu#popup) */
    popupProps?: BaseMenu.Popup.Props;
    /** Override the default z-index. Defaults to 111. */
    zIndex?: number;
  };

  // sub-component props
  export type ItemProps = MenuItem.Props;
  export type ItemLinkProps = MenuItemLink.Props;
  export type SeparatorProps = MenuSeparator.Props;
}

/**
 * =====================================
 * Menu Button
 *
 * This sets up the default menu trigger
 * which is a Button with text and a
 * down arrow.
 * =====================================
 * */
export const MenuTrigger = ({
  className,
  text,
  children,
  typographyProps,
  ...props
}: MenuTrigger.Props) => {
  return (
    <BaseMenu.Trigger
      className={clsx(styles.button, className)}
      render={(renderProps) => {
        return (
          <Typography
            textVariant="body2"
            {...typographyProps}
            renderAs={Button}
            {...(renderProps as MenuTrigger.ButtonProps)}
          >
            {text}
            <ActionsArrowDown
              data-has-text={!!text}
              className={styles.buttonIcon}
            />
          </Typography>
        );
      }}
      {...props}
    >
      {children}
    </BaseMenu.Trigger>
  );
};
MenuTrigger.displayName = 'MenuTrigger';

export namespace MenuTrigger {
  export type ButtonProps = Omit<Button.Props, 'children' | 'color'>;
  export type Props = BaseMenu.Trigger.Props & {
    /** The trigger button text. */
    text?: string;
    /** Props to pass to the Typography component. */
    typographyProps?: Typography.Props<'button'>;
  } & ButtonProps;
}
/**
 * =====================================
 * Menu List
 *
 * Composes the Portal, Positioner and
 * Popup elements from base-ui
 * To render a List that can hold the
 * Menu List Items
 * =====================================
 * */
export const MenuList = ({
  className,
  positionerProps,
  portalProps,
  children,
  zIndex,
  ...props
}: MenuList.Props) => {
  const {
    className: positionerClassName,
    style: positionerStyle,
    ...restPositionerProps
  } = positionerProps ?? {};

  return (
    <BaseMenu.Portal {...portalProps}>
      <BaseMenu.Positioner
        className={clsx(styles.positioner, positionerClassName)}
        align="start"
        render={({ style, ...renderProps }) => (
          <div
            {...renderProps}
            style={
              {
                ...positionerStyle,
                ...style,
                '--z-index-override': zIndex
              } as CssPropsWithVars
            }
          />
        )}
        {...restPositionerProps}
      >
        <BaseMenu.Popup {...props} className={clsx(className, styles.popup)}>
          {children}
        </BaseMenu.Popup>
      </BaseMenu.Positioner>
    </BaseMenu.Portal>
  );
};
MenuList.displayName = 'MenuList';

export namespace MenuList {
  export type Props = BaseMenu.Popup.Props & {
    /** Props passed to the base-ui positioner component. [See Positioner Arg Table](https://base-ui.com/react/components/menu#positioner) */
    positionerProps?: BaseMenu.Positioner.Props;
    /** Props passed to the base-ui portal component. [See Portal Arg Table](https://base-ui.com/react/components/menu#portal) */
    portalProps?: BaseMenu.Portal.Props;
    /** Override the default z-index. Defaults to 111. */
    zIndex?: number;
  };
}

/**
 * =====================================
 * Menu Item
 *
 * An individual interactive item in the menu.
 * Renders a <div> element.
 * =====================================
 * */
export const MenuItem = ({
  className,
  children,
  shouldRenderSeparator = true,
  separatorProps,
  ...props
}: MenuItem.Props) => {
  return (
    <>
      <BaseMenu.Item className={clsx(className, styles.item)} {...props}>
        {children}
      </BaseMenu.Item>
      {shouldRenderSeparator && <MenuSeparator {...separatorProps} />}
    </>
  );
};
MenuItem.displayName = 'MenuItem';

export namespace MenuItem {
  // remove support for disabled menu items since we don't
  // have existing use cases or design input
  export type Props = Omit<BaseMenu.Item.Props, 'disabled'> & {
    /** Whether to render a separator after the item. Defaults to true. */
    shouldRenderSeparator?: boolean;
    /** Props to pass to the MenuSeparator subcomponent. [See Separator Arg Table](https://base-ui.com/react/components/menu#separator) */
    separatorProps?: MenuSeparator.Props;
  };
}

/**
 * =====================================
 * Menu Item Link
 *
 * An individual interactive item in the menu.
 * Renders an <a> element.
 * =====================================
 * */
export const MenuItemLink = ({
  className,
  children,
  ...props
}: MenuItemLink.Props) => {
  return (
    <MenuItem
      {...props}
      render={(renderProps) => {
        return (
          <Element
            {...(renderProps as Element.Props<'a'>)}
            className={clsx(className, styles.item, styles.linkItem)}
            renderAs="a"
          >
            {children}
          </Element>
        );
      }}
    />
  );
};
MenuItemLink.displayName = 'MenuItemLink';

export namespace MenuItemLink {
  export type Props = MenuItem.Props & Element.Props<'a'>;
}

/**
 * =====================================
 * Menu Separator
 *
 * Creates a visible separation between
 * Menu items or sections.
 * A gray line by default
 * =====================================
 * */
export const MenuSeparator = ({
  className,
  children,
  ...props
}: MenuSeparator.Props) => {
  return (
    <BaseMenu.Separator
      className={clsx(className, styles.separator)}
      {...props}
    >
      {children}
    </BaseMenu.Separator>
  );
};
MenuSeparator.displayName = 'MenuSeparator';

export namespace MenuSeparator {
  export type Props = BaseMenu.Separator.Props;
}

/**
 * =====================================
 * Compound Components
 * =====================================
 * */
Menu.Item = MenuItem;
Menu.ItemLink = MenuItemLink;
Menu.Separator = MenuSeparator;
